import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Single-size garment measurement lookup.
 *
 * Every size's measurement panel is rendered server-side inside `refs.panels`
 * with a matching `data-size` attribute when the full lookup is requested.
 * The same component can render the shared unit and size controls without the
 * lookup panels for the Size Guide composition. Each value retains its
 * centimetre source in `data-cm`, so changing units never converts an
 * already-converted number.
 *
 * The default size's panel is already visible in the server-rendered markup,
 * so this component only takes over the switching:
 * the shopper's own size is readable before this script has run.
 *
 * @typedef {object} Refs
 * @property {HTMLSelectElement} select
 * @property {HTMLElement | undefined} panels
 *
 * @extends {Component<Refs>}
 */
export class GarmentDimensions extends Component {
  static CM_PER_INCH = 2.54;

  requiredRefs = ['select'];

  /** @type {HTMLElement | null} */
  #section;

  /**
   * Handles the `<select>` changing to a different size.
   *
   * Bound declaratively via `on:change="/onSizeChange"` on the host element,
   * so Horizon's shared event delegation supplies the event rather than this
   * component managing its own listener.
   */
  onSizeChange() {
    const size = this.refs.select.value;

    if (this.refs.panels) this.#showMatching(this.refs.panels, size);
    this.#highlightConversionRow(size);
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onUnitClick);
    this.#section = this.closest('.shopify-section');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
    // The conversion chart's server-rendered highlight follows the variant,
    // not this control — sync it so the two never open disagreeing.
    this.#highlightConversionRow(this.refs.select.value);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onUnitClick);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#section = null;
    super.disconnectedCallback();
  }

  /**
   * Keeps the measurements lookup aligned with the PDP's current variant.
   * The variant picker emits this before its Section Rendering API request,
   * so the panel is already correct when the shopper opens it.
   *
   * @param {ProductSelectEvent} event - The PDP variant selection event.
   */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || !event.target.matches('variant-picker')) return;

    const selectedSize = event.selectedOptions.find(({ name }) => name.toLowerCase().includes('size'))?.value;
    if (!selectedSize) return;

    const matchingOption = Array.from(this.refs.select.options).find(
      (option) => option.value.toLowerCase() === selectedSize.toLowerCase(),
    );
    if (!matchingOption || this.refs.select.value === matchingOption.value) return;

    this.refs.select.value = matchingOption.value;
    this.onSizeChange();
  };

  /**
   * @param {MouseEvent} event - A click within the measurement controls.
   */
  #onUnitClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-unit-option]') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    const unit = button.dataset.unitOption;
    if (unit !== 'cm' && unit !== 'in') return;

    this.setUnit(unit);
  };

  /**
   * @param {'cm' | 'in'} unit - Unit to display in the active size panel.
   */
  setUnit(unit) {
    for (const button of this.querySelectorAll('[data-unit-option]')) {
      button.setAttribute('aria-pressed', String(button.getAttribute('data-unit-option') === unit));
    }

    for (const cell of this.querySelectorAll('[data-cm]')) {
      const cm = Number.parseFloat(cell.getAttribute('data-cm') ?? '');
      if (Number.isNaN(cm)) continue;

      cell.textContent = unit === 'cm' ? String(cm) : (cm / GarmentDimensions.CM_PER_INCH).toFixed(1);
    }

    for (const label of this.querySelectorAll('[data-unit-label]')) {
      label.textContent = unit.toUpperCase();
    }

    this.dataset.unit = unit;
  }

  /**
   * Moves the international conversion chart's highlighted row to the size
   * shown in the select. The chart sits in the same panel section on the
   * Size Guide tab; catalogue labels 2XS/2XL map to the chart's XXS/XXL.
   *
   * @param {string} size - The catalogue size label shown in the select.
   */
  #highlightConversionRow(size) {
    const chart = this.closest('.panel-section')?.querySelector('.size-conversion');
    if (!chart) return;

    let normalised = size.trim().toUpperCase();
    if (normalised === '2XS') normalised = 'XXS';
    if (normalised === '2XL') normalised = 'XXL';

    for (const row of chart.querySelectorAll('tbody tr')) {
      const label = row.querySelector('th')?.textContent?.trim().toUpperCase() ?? '';
      if (label === normalised) row.setAttribute('aria-current', 'true');
      else row.removeAttribute('aria-current');
    }
  }

  /**
   * Shows the child of `container` whose `data-size` matches `size` and
   * hides every other child.
   *
   * @param {HTMLElement} container - Element holding one child per size.
   * @param {string} size - The size to reveal.
   */
  #showMatching(container, size) {
    for (const child of container.children) {
      if (!(child instanceof HTMLElement)) continue;

      child.hidden = child.dataset.size !== size;
    }
  }
}

if (!customElements.get('garment-dimensions')) {
  customElements.define('garment-dimensions', GarmentDimensions);
}
